/**
 * ═══════════════════════════════════════════════════════════════════════════
 *  CONSENT ARTEFACT BUILDER
 * ═══════════════════════════════════════════════════════════════════════════
 *
 *  Turns the tick-boxes a patient saw into the record that proves what they
 *  agreed to. Two things make the record defensible rather than decorative:
 *
 *    NOTICE HASH — we hash the exact text rendered on screen. If a patient
 *    later disputes what they consented to, the hash ties their artefact to
 *    one specific wording without storing a copy of the notice per patient.
 *
 *  The notice is served in English only (product decision, 2026-08), so the
 *  artefact no longer records a notice language.
 */

import {
  INDIA_CONSENT_ITEMS,
  INDIA_CONSENT_BUNDLE_VERSION,
  type IndiaConsentId,
  type IndiaConsentItem,
} from './indiaConsentRegistry';
import type {
  ConsentArtefactRequest,
  ConsentDecision,
  ConsentMethod,
} from 'src/shared/Services/apiService/ConsentTypes';

/**
 * Canonical serialisation of what was on screen.
 *
 * Order matters and must be stable, so the same notice always hashes the same.
 */
const canonicaliseNotice = (items: IndiaConsentItem[]): string =>
  [
    `bundle:${INDIA_CONSENT_BUNDLE_VERSION}`,
    ...items
      .slice()
      .sort((a, b) => a.id.localeCompare(b.id))
      .map((i) =>
        [
          i.id,
          i.version,
          i.required ? 'required' : 'optional',
          i.label,
          i.summary,
          i.purpose,
          i.dataItems.join('|'),
          i.recipients.join('|'),
          i.legalBasis,
          i.consequenceOfDeclining,
          i.retention,
        ].join('~'),
      ),
  ].join('\n');

/** Non-cryptographic fallback for contexts without SubtleCrypto (rare: http://). */
const weakHash = (input: string): string => {
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < input.length; i += 1) {
    const ch = input.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return `weak-${(h2 >>> 0).toString(16).padStart(8, '0')}${(h1 >>> 0).toString(16).padStart(8, '0')}`;
};

/** SHA-256 of the canonical notice, hex encoded. */
export const hashNotice = async (items: IndiaConsentItem[]): Promise<string> => {
  const canonical = canonicaliseNotice(items);

  if (!globalThis.crypto?.subtle) return weakHash(canonical);

  try {
    const bytes = new TextEncoder().encode(canonical);
    const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes);
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  } catch {
    return weakHash(canonical);
  }
};

export interface BuildArtefactArgs {
  /** Checkbox state, keyed by consent id. */
  decisions: Record<string, boolean>;
  /** Items that were actually rendered — the artefact covers only these. */
  items?: IndiaConsentItem[];
  method: ConsentMethod;
  surface: string;
  guardian?: ConsentArtefactRequest['guardian'];
}

/**
 * Build the payload for `apiService.recordConsent`.
 *
 * Every rendered item produces a decision — including the ones left unticked.
 * Recording an explicit `false` is what lets us prove the patient was offered
 * the optional item and declined it, rather than never being asked.
 */
export const buildConsentArtefact = async ({
  decisions,
  items = INDIA_CONSENT_ITEMS,
  method,
  surface,
  guardian,
}: BuildArtefactArgs): Promise<ConsentArtefactRequest> => {
  const noticeHash = await hashNotice(items);

  const decisionList: ConsentDecision[] = items.map((item) => ({
    consentId: item.id,
    granted: decisions[item.id] === true,
    itemVersion: item.version,
  }));

  return {
    bundleVersion: INDIA_CONSENT_BUNDLE_VERSION,
    jurisdiction: 'IN',
    noticeHash,
    method,
    decisions: decisionList,
    surface,
    clientRecordedAt: new Date().toISOString(),
    ...(guardian ? { guardian } : {}),
  };
};

/**
 * Does the patient need to be re-prompted?
 *
 * True when they have never consented, or when they consented to an older
 * bundle. A version bump is how we re-obtain consent after the notice changes,
 * which DPDP requires — old consent does not cover new wording.
 */
export const needsReconsent = (acceptedBundleVersion: string | null): boolean =>
  acceptedBundleVersion !== INDIA_CONSENT_BUNDLE_VERSION;

/** Effective state → the subset of ids currently granted. */
export const grantedIds = (effective: Record<string, boolean>): IndiaConsentId[] =>
  INDIA_CONSENT_ITEMS.filter((i) => effective[i.id]).map((i) => i.id);
